import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { enumTransportationMode } from '@app/data-layer/enums/entry-summaries';
import { ShipmentModel, UnitModel } from '@app/data-layer/models/common';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { CodeRepository } from '@app/data-layer/repositories';
import { IBillOfLadingFormData } from '@app/modules/entry-summary/interfaces';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { FormHelper } from '@helpers/form.helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { esBlFormData } from '@shared/es-mutual/const';
import {
  EntrySummaryStateService,
  EsCreateBlFormService,
  EsOcrQuickFixService,
  EsValidationsHelperService,
} from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { ReplaySubject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-bl-details',
  templateUrl: './es-ocr-quick-fix-bl-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-bl-details.component.scss'],
})
export class EsOcrQuickFixBlDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;

  @Input() public shipmentId: number;
  @Input() public isCanEdit: boolean;
  @Input() public masterScacFields: ISectionFormField[];
  @Input() public masterBlFields: ISectionFormField[];
  @Input() public houseScacFields: ISectionFormField[];
  @Input() public houseBlFields: ISectionFormField[];
  @Input() public quantityFields: ISectionFormField[];
  @Input() public itFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public shipment: ShipmentModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public billOfLadingData: IBillOfLadingFormData;
  public decimalInputData: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public isImmediateTransfer: boolean = false;
  public hasSingleExportData: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private codeRepository: CodeRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private esCreateBlFormService: EsCreateBlFormService,
    private entrySummaryStateService: EntrySummaryStateService,
    private esValidationsHelperService: EsValidationsHelperService,
  ) {}

  ngOnInit(): void {
    if (this.isAnyFieldBlocksMissing) {
      return;
    }

    this.entrySummary = this.esOcrQuickFixService.getEntrySummary();
    this.shipment = this.entrySummary?.shipments.find((shipment) => shipment.id === this.shipmentId);

    this.formGroup = this.esCreateBlFormService.createForm(false);
    this.esCreateBlFormService.setData(this.entrySummary, this.shipment, this.isCanEdit);
    this.esCreateBlFormService.updateShipmentForm(this.entrySummary, false);

    this.onUpdateEntrySummary();

    this.setBlData();
    this.setFieldsProperties();

    this.populateDisplayErrors();
    this.createFormErrors();

    this.updateTruckValidation();

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[]): boolean {
    let invalidFields = blockFields.find((field) => this.formGroup.get(field.name)?.invalid || !this.formGroup.get(field.name)?.value);
    return !!invalidFields;
  }

  public formFieldError(value: string | string[]): string {
    const fieldName = Array.isArray(value) ? value.join('-') : value;
    return this.displayErrors.get(fieldName) || '';
  }

  public onSubmit(needValidate: boolean = true) {
    if (needValidate && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      this.openMissingFieldsModal(invalidFields);
      return;
    }

    this.entrySummaryStateService
      .updateShipment(this.shipment, this.formGroup.value)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.dataSaved.emit(this.formGroup.value);
      });
  }

  public get isRail(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  private openMissingFieldsModal(data: any): void {
    this.missingFieldsModal.openCustomModal(
      this.esValidationsHelperService.getEsSummaryModalModifiedData(this.entrySummary.transportationMode, data),
    );
  }

  private setBlData() {
    if (this.entrySummary?.transportationMode) {
      this.billOfLadingData = esBlFormData[this.entrySummary?.transportationMode] || esBlFormData.default;
    } else {
      this.billOfLadingData = esBlFormData[enumTransportationMode.OCEAN];
    }
  }

  private setFieldsProperties() {
    this.masterScacFields.forEach((field) => {
      field.label = this.billOfLadingData.carrier.titleMaster;
      field.helper = this.billOfLadingData.carrier.helper;
      field.pattern = this.billOfLadingData.carrier.patternMaster;
      field.mask = this.billOfLadingData.carrier.maskMaster;
      field.inputMode = this.billOfLadingData.carrier.inputmodeMaster;
      field.maxMaskLength = this.billOfLadingData.carrier.maxMaskLength;
      field.maskErrorTooltip = this.billOfLadingData.carrier.maskMessageMaster;
    });

    this.masterBlFields.forEach((field) => {
      field.label = this.billOfLadingData.number.titleMaster;
      field.helper = this.billOfLadingData.number.helper;
      field.pattern = this.billOfLadingData.number.patternMaster;
      field.mask = this.billOfLadingData.number.maskMaster;
      field.inputMode = this.billOfLadingData.number.inputmodeMaster;
      field.maxMaskLength = this.billOfLadingData.number.maxMaskLength;
      field.maskErrorTooltip = this.billOfLadingData.number.maskMessageMaster;
    });

    this.houseScacFields.forEach((field) => {
      field.label = this.billOfLadingData.carrier.titleHouse;
      field.helper = this.billOfLadingData.carrier.helper;
      field.pattern = this.billOfLadingData.carrier.patternHouse;
      field.mask = this.billOfLadingData.carrier.maskHouse;
      field.inputMode = this.billOfLadingData.carrier.inputmodeHouse;
      field.maxMaskLength = this.billOfLadingData.carrier.maxMaskLength;
      field.maskErrorTooltip = this.billOfLadingData.carrier.maskMessageHouse;
    });

    this.houseBlFields.forEach((field) => {
      field.label = this.billOfLadingData.number.titleHouse;
      field.helper = this.billOfLadingData.number.helper;
      field.pattern = this.billOfLadingData.number.patternHouse;
      field.mask = this.billOfLadingData.number.maskHouse;
      field.inputMode = this.billOfLadingData.number.inputmodeHouse;
      field.maxMaskLength = this.billOfLadingData.number.maxMaskLength;
      field.maskErrorTooltip = this.billOfLadingData.number.maskMessageHouse;
    });

    this.quantityFields.forEach((field) => {
      switch (field.name) {
        case 'quantity':
          field.pattern = this.decimalInputData.pattern;
          field.mask = this.decimalInputData.mask;
          field.maskErrorTooltip = this.decimalInputData.maskErrorTooltip;
          break;
        case 'quantityUnit':
          const setItems = (quantityUnits: UnitModel[]) => {
            field.items = quantityUnits;
            field.searchFn = this.searchQuantityUnit;
          };
          this.getShipmentUnits(setItems);
          break;

        default:
          break;
      }
    });

    this.itFields.forEach((field) => {
      switch (field.name) {
        case 'itDate':
          field.minDate = this.itMinDate;
          field.maxDate = this.itMaxDate;
          break;
        default:
          break;
      }
    });
  }

  private populateDisplayErrors() {
    Object.keys(this.formGroup.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'masterBill':
        case 'houseBill':
          const fieldFormGroup: FormGroup = this.formGroup.controls[fieldName]['controls'];
          Object.keys(fieldFormGroup).forEach((subFieldName) => {
            const displayErrorKeyName = `${fieldName}${subFieldName[0].toUpperCase() + subFieldName.substring[1]}`;
            this.displayErrors.set(displayErrorKeyName, this.fieldMissingErrorText([fieldName, subFieldName]));
          });
          break;
        case 'quantity':
        case 'quantityUnit':
        case 'itNumber':
        case 'itDate':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName));
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = {
      masterBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      masterBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsHouseBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentHBLMatch: 'FORM_ERROR.SHIPMENT_MBL_MATCH_TO_HBL',
        shipmentBLMatchSCAC: 'FORM_ERROR.SHIPMENT_BL_MATCH_SCAC',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillCarrier: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      houseBillNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
        sameAsMasterBillNumber: 'FORM_ERROR.SAME_BL_OCEAN',
        shipmentBLMatch: 'FORM_ERROR.SHIPMENT_HBL_NOT_UNIQUE',
        minlength: 'FORM_ERROR.MIN_LENGTH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      quantity: {
        required: 'FORM_ERROR.REQUIRED',
      },
      quantityUnit: {
        required: 'FORM_ERROR.REQUIRED',
      },
    };
  }

  private updateTruckValidation(): void {
    if (this.isTruck) {
      this.formGroup.get('chargesDistributionType').clearValidators();

      if (!this.isWithVia) {
        this.formGroup.get('itDate').clearValidators();
        this.formGroup.get('itNumber').clearValidators();
      }
    }
  }

  private fieldMissingErrorText(fieldName: string | string[]): string {
    return !this.formGroup.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private getShipmentUnits(callback?: (quantityUnits: UnitModel[]) => void) {
    this.codeRepository
      .getShipmentUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe((quantityUnits: UnitModel[]) => {
        callback(quantityUnits);
      });
  }

  private onUpdateEntrySummary() {
    const { portOfEntryCode, portOfUnladingCode } = this.entrySummary;
    this.hasSingleExportData = this.entrySummary?.hasSingleExportData;
    this.isImmediateTransfer = !this.isTruck
      ? portOfEntryCode !== portOfUnladingCode || !portOfEntryCode || !portOfUnladingCode
      : this.isWithVia;
  }

  private searchQuantityUnit(term: string, item: UnitModel) {
    term = term.toLowerCase().trim();
    return item.code.toLowerCase().indexOf(term) > -1 || item.description.toLowerCase().indexOf(term) > -1;
  }

  private get itMinDate(): string {
    return this.entrySummaryStateService.itMinDate(
      this.entrySummary,
      this.hasSingleExportData ? this.formGroup.get('exportDate').value : null,
    );
  }

  private get itMaxDate(): string {
    return this.entrySummaryStateService.itMaxDate(this.entrySummary);
  }

  private get isWithVia(): boolean {
    return this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode;
  }

  private get isAnyFieldBlocksMissing(): boolean {
    return (
      !this.masterScacFields ||
      !this.masterBlFields ||
      !this.houseScacFields ||
      !this.houseBlFields ||
      !this.quantityFields ||
      !this.itFields
    );
  }
}
